import { SearchFilters } from '../api/courses';

import { GRADE_LEVELS, SUBJECTS } from './catalog';

/**
 * What the catalog shows before anyone narrows it.
 *
 * Named once so the first load, Reset and an empty query string cannot
 * disagree — PRT-02-FR-04 asks for a reset that clears the filters, and a
 * reset written by hand clears the ones its author remembered.
 */
export const NO_FILTERS: SearchFilters = { sortBy: 'soonest' };

/** The sort orders the catalog offers. Anything else is a stale or forged URL. */
export const SORT_VALUES: NonNullable<SearchFilters['sortBy']>[] = [
  'soonest',
  'newest',
  'price_low',
  'price_high',
];

/**
 * The filters that narrow the catalog, as opposed to reordering it.
 *
 * Sort is not one of them: a catalog showing everything in price order is not
 * a filtered catalog, and counting it would offer a Reset on an untouched
 * screen.
 */
export const narrowingFilters = (filters: SearchFilters): (keyof SearchFilters)[] =>
  (['subject', 'level', 'maxPrice'] as (keyof SearchFilters)[]).filter((key) => {
    const value = filters[key];
    return value !== undefined && value !== '';
  });

/**
 * The catalog's state written into the address bar (PRT-02-FR-12, PRT-BR-P03-08).
 *
 * A course card used to link to `/courses/:id` and the detail page's back
 * button went to `/courses`, so a parent who filtered to three courses, opened
 * one and came back got the whole catalog again — the filter context the
 * handoff to P04 is supposed to preserve was thrown away on the way there.
 *
 * It carries filters only. No learner id, no name: this string ends up in a
 * shared link (QA-005).
 */
export const filtersToParams = (filters: SearchFilters): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters.subject) params.set('subject', filters.subject);
  if (filters.level) params.set('level', filters.level);
  if (filters.maxPrice !== undefined) params.set('maxPrice', String(filters.maxPrice));
  // Only when it is not the default, so an untouched catalog has a clean URL.
  if (filters.sortBy && filters.sortBy !== NO_FILTERS.sortBy) params.set('sort', filters.sortBy);
  return params;
};

/**
 * Reads them back, dropping anything the governed registries do not admit.
 *
 * A11 — a deep link written against last month's taxonomy carries a subject
 * that no longer exists. Dropping the unknown value and keeping the rest is
 * what A3 asks for: normalise without deleting valid unrelated criteria.
 * Keeping it would filter the catalog by something no course can match and
 * read as "there are no courses".
 */
export const filtersFromParams = (params: URLSearchParams): SearchFilters => {
  const subject = params.get('subject');
  const level = params.get('level');
  const maxPrice = params.get('maxPrice');
  const sort = params.get('sort');
  const price = maxPrice === null ? NaN : Number(maxPrice);

  return {
    ...NO_FILTERS,
    ...(subject && (SUBJECTS as readonly string[]).includes(subject) ? { subject } : {}),
    ...(level && (GRADE_LEVELS as readonly string[]).includes(level) ? { level } : {}),
    ...(Number.isFinite(price) && price > 0 ? { maxPrice: price } : {}),
    ...(sort && (SORT_VALUES as string[]).includes(sort)
      ? { sortBy: sort as SearchFilters['sortBy'] }
      : {}),
  };
};

/** Where a course card points, with the result set it was selected from. */
export const courseDetailPath = (listingId: string, filters: SearchFilters): string => {
  const params = filtersToParams(filters);
  const query = params.toString();
  return query ? `/courses/${listingId}?${query}` : `/courses/${listingId}`;
};

/** Where "back to results" goes, given whatever the detail page was opened with. */
export const catalogPath = (params: URLSearchParams): string => {
  const query = filtersToParams(filtersFromParams(params)).toString();
  return query ? `/courses?${query}` : '/courses';
};
